import React, { useRef, useEffect, useCallback } from 'react'
import bowser from 'bowser'

import {
  useQueryString,
  useLiveRealtimeQuery,
} from '../../../sharedLegacy/hooks'
import { SimplePubSubClientProvider } from '../../../sharedLegacy/providers/simple-pub-sub-client-provider'
import {
  JoinToSpeakProvider,
  JoinToSpeakRole,
  useHasJoinedToSpeak,
} from '../../../sharedLegacy/join-to-speak'
import useFeatures from '../../../sharedLegacy/hooks/useFeatures'
import usePreconnect from '../../hooks/usePreconnect'
import AudienceFoyer from '../AudienceFoyerComponent/AudienceFoyerComponent'
import WebinarAudienceRoomContainer from '../WebinarAudienceRoomContainerComponent/WebinarAudienceRoomContainerComponent'
import { useAudienceAppState } from '../../providers/AudienceAppStateProvider'
import WebinarConnectingComponent from '../../../Webinar/components/WebinarConnectingComponent/WebinarConnectingComponent'

import AudienceFoyerOptions from './AudienceFoyerOptionsComponent'

const playSilence = () => {
  const silence = document.getElementById('silence')
  if (silence) {
    silence.play()
  }
}

const AudienceAppComponent = () => {
  const {
    setError,
    scope,
    setScope,
    liveId,
    liveToken,
    publicChannelURL,
    setPublicChannelURL,
  } = useAudienceAppState()
  const joiningToSpeakTimeoutRef = useRef(null)
  const { load } = usePreconnect(liveId, liveToken, setError)
  const [customizeQueryParam] = useQueryString('customize')
  const [fastForwardQueryParam] = useQueryString('ff')
  const { HasJoinToSpeakEnabled } = useFeatures()
  const { data: realtime } = useLiveRealtimeQuery({
    liveId,
    token: window.LIVE_TOKEN,
    includePrivate: false,
  })
  const [hasJoinedToSpeak, setHasJoinedToSpeak] = useHasJoinedToSpeak(liveId)

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (hasJoinedToSpeak) {
      if (joiningToSpeakTimeoutRef.current !== null) {
        clearTimeout(joiningToSpeakTimeoutRef.current)
      }

      const timeout = setTimeout(() => {
        setHasJoinedToSpeak(false)
        joiningToSpeakTimeoutRef.current = null
      }, 1000)

      joiningToSpeakTimeoutRef.current = timeout
    }
  }, [hasJoinedToSpeak, setHasJoinedToSpeak])

  useEffect(() => {
    /**
     * Hack borrowed from the original audience app room.
     */
    document.body.classList.add('react-audience-room')

    /**
     * We are intentionally skipping the foyer on mobile, for now. We set the
     *  scope to audience like we would if they went through the foyer.
     */
    if (bowser.mobile) {
      setScope('audience')
    }
  }, [setScope])

  /**
   * Should be able to hide the foyer when you are in room customization mode.
   *  Right now the scope of the application is set to customize, but since the
   *  scope really isn't used to anything yet in the audience application, it's
   *  just for identifing the scope priviledges for good measure.
   */
  useEffect(() => {
    if (customizeQueryParam) {
      setScope('customize')
    }
  }, [customizeQueryParam, setScope])

  useEffect(() => {
    if (fastForwardQueryParam) {
      setScope('audience')
    }
  }, [fastForwardQueryParam, setScope])

  useEffect(() => {
    if (realtime !== undefined) {
      setPublicChannelURL(realtime.publicChannelURL)
    }
  }, [realtime, setPublicChannelURL])

  const onOptionClick = useCallback(
    (option) => () => {
      /**
       * Whenever an audience member selects an option, we make sure to play a
       *  silent video tricking the browser into believing that we want to
       *  receive audio and that way the webinar stream will start unmuted.
       */
      playSilence()
      setScope(option)
    },
    [setScope]
  )

  if (hasJoinedToSpeak) {
    return (
      <WebinarConnectingComponent
        isModalOpen
        loadingText="We are taking you back to the audience"
      />
    )
  }

  if (!scope) {
    return (
      <AudienceFoyer
        onError={setError}
        renderOptions={(name, link) => (
          <AudienceFoyerOptions
            onOptionClick={onOptionClick}
            fromLink={link}
            name={name}
          />
        )}
      />
    )
  }

  if (!realtime?.realtimeURL || publicChannelURL === null) {
    return null
  }

  const joinToSpeakRole = ['audience', 'customize'].includes(scope)
    ? JoinToSpeakRole.Audience
    : null

  return (
    <SimplePubSubClientProvider url={realtime.realtimeURL}>
      <JoinToSpeakProvider
        isEnabled={HasJoinToSpeakEnabled}
        role={joinToSpeakRole}
        {...(joinToSpeakRole === JoinToSpeakRole.Audience
          ? { sessionId: liveId, token: liveToken }
          : { sessionId: liveId })}
      >
        <WebinarAudienceRoomContainer />
      </JoinToSpeakProvider>
    </SimplePubSubClientProvider>
  )
}

export { AudienceAppComponent }
